import { useEffect, useRef } from "react";
import { fetchMeetingStatus } from "../api/meetingKioskApi";
import { applyMeetingCapacity } from "../applyMeetingCapacity";
import { reconcileAgendaItemStatuses } from "../statusReconcile";
import { ITEMS_CLOSED_NOTICE } from "../utils";
import type { MeetingData, Stance } from "../types";

interface UseStatusPollArgs {
  statusUrl: string | undefined;
  data: MeetingData | null;
  setData: React.Dispatch<React.SetStateAction<MeetingData | null>>;
  selections: Record<number, Stance>;
  setSelections: React.Dispatch<React.SetStateAction<Record<number, Stance>>>;
  done: boolean;
  setClosedNotice: React.Dispatch<React.SetStateAction<string | null>>;
  refreshKey?: string;
}

export function useStatusPoll({
  statusUrl,
  data,
  setData,
  selections,
  setSelections,
  done,
  setClosedNotice,
  refreshKey
}: UseStatusPollArgs): void {
  const dataRef = useRef<MeetingData | null>(null);
  const selectionsRef = useRef<Record<number, Stance>>(selections);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    selectionsRef.current = selections;
  }, [selections]);

  const canPoll = !!data && !done && !!statusUrl;

  useEffect(() => {
    if (!canPoll) return;
    let cancelled = false;

    const poll = async () => {
      let status;
      try {
        status = await fetchMeetingStatus(statusUrl!);
      } catch {
        return;
      }
      if (cancelled) return;
      const current = dataRef.current;
      if (!current) return;

      const { items, changed, newlyClosedIds } = reconcileAgendaItemStatuses(
        current.agendaItems,
        status.agendaItems ?? []
      );

      const meeting = current.meeting
        ? applyMeetingCapacity(current.meeting, status.meeting)
        : current.meeting;

      if (changed || meeting !== current.meeting) {
        setData({
          ...current,
          agendaItems: changed ? items : current.agendaItems,
          meeting
        });
      }

      if (newlyClosedIds.length === 0) return;
      const closedSelected = newlyClosedIds.filter(
        (id) => selectionsRef.current[id] != null
      );
      if (closedSelected.length === 0) return;

      const dropped = new Set(closedSelected);
      setSelections((s) => {
        const nextSel: Record<number, Stance> = {};
        Object.keys(s).forEach((k) => {
          const id = Number(k);
          if (!dropped.has(id)) nextSel[id] = s[id];
        });
        return nextSel;
      });
      setClosedNotice(ITEMS_CLOSED_NOTICE);
    };

    poll();
    const t = window.setInterval(poll, 30 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [canPoll, statusUrl, refreshKey, setData, setSelections, setClosedNotice]);
}
